import { useEffect, useRef } from 'react';

const SEED = 42;

function seededRandom(s) {
  let t = s;
  return () => {
    t = (t * 16807 + 0) % 2147483647;
    return (t - 1) / 2147483646;
  };
}

const rand = seededRandom(SEED);

const W = 1440;
const H = 960;

function generateChips() {
  const chips = [];
  for (let i = 0; i < 8; i++) {
    const cx = 100 + rand() * (W - 200);
    const cy = 100 + rand() * (H - 200);
    const cols = 2 + Math.floor(rand() * 3);
    const rows = 1 + Math.floor(rand() * 3);
    const pinW = 6;
    const pinH = 4;
    const pinGap = 12;
    const bodyPad = 8;
    const bodyW = Math.max(cols * (pinW + pinGap) + bodyPad * 2, 60);
    const bodyH = rows * (pinH + pinGap) + bodyPad * 2;
    const x = Math.max(bodyW / 2, Math.min(W - bodyW / 2, cx));
    const y = Math.max(bodyH / 2, Math.min(H - bodyH / 2, cy));

    // Min distance from other chips
    let tooClose = false;
    for (const c of chips) {
      const dx = x - c.x;
      const dy = y - c.y;
      if (Math.hypot(dx, dy) < (bodyW + c.bodyW) / 2 + 80) {
        tooClose = true;
        break;
      }
    }
    if (tooClose) continue;

    chips.push({ x, y, bodyW, bodyH, cols, rows, pinW, pinH, pinGap, bodyPad });
  }
  return chips;
}

function generateTraces(chips, count) {
  const traces = [];
  for (let i = 0; i < count; i++) {
    const a = chips[Math.floor(rand() * chips.length)];
    let b = chips[Math.floor(rand() * chips.length)];
    if (a === b) continue;

    // Use pin positions on the chip bodies
    const ax = a.x + (rand() - 0.5) * a.bodyW * 0.8;
    const ay = a.y + (rand() - 0.5) * a.bodyH * 0.8;
    const bx = b.x + (rand() - 0.5) * b.bodyW * 0.8;
    const by = b.y + (rand() - 0.5) * b.bodyH * 0.8;

    // Manhattan + diagonal routing
    const midX = ax + (bx - ax) * (0.3 + rand() * 0.4);
    const midY = ay + (by - ay) * (0.3 + rand() * 0.4);

    const pathType = Math.floor(rand() * 3);
    let d;
    if (pathType === 0) {
      d = `M${ax},${ay} L${midX},${ay} L${midX},${by} L${bx},${by}`;
    } else if (pathType === 1) {
      d = `M${ax},${ay} L${ax},${midY} L${bx},${midY} L${bx},${by}`;
    } else {
      d = `M${ax},${ay} L${midX},${ay} L${midX},${midY} L${bx},${midY} L${bx},${by}`;
    }

    traces.push({
      d,
      length: estimatePathLength(d),
      delay: rand() * 8,
      duration: 3 + rand() * 6,
    });
  }
  return traces;
}

function estimatePathLength(d) {
  const pts = d.match(/[\d.]+,[\d.]+/g) || [];
  let len = 0;
  for (let i = 1; i < pts.length; i++) {
    const [x1, y1] = pts[i - 1].split(',').map(Number);
    const [x2, y2] = pts[i].split(',').map(Number);
    len += Math.hypot(x2 - x1, y2 - y1);
  }
  return len;
}

const chips = generateChips();
const traces = generateTraces(chips, 28);

const viaNodes = [];
for (let i = 0; i < 20; i++) {
  viaNodes.push({
    cx: 60 + rand() * (W - 120),
    cy: 60 + rand() * (H - 120),
    r: 2 + rand() * 3,
  });
}

export default function CircuitBackground() {
  const containerRef = useRef(null);

  useEffect(() => {
    // No resize needed; SVG viewBox handles scaling
  }, []);

  return (
    <div ref={containerRef} className="circuit-bg" aria-hidden="true">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
        className="circuit-svg"
      >
        <defs>
          <radialGradient id="cbg1" cx="20%" cy="20%">
            <stop offset="0%" stopColor="var(--circuit-wash-a)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="cbg2" cx="80%" cy="70%">
            <stop offset="0%" stopColor="var(--circuit-wash-b)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="cbg3" cx="50%" cy="50%">
            <stop offset="0%" stopColor="var(--circuit-wash-c)" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="glowStrong">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Subtle gradient washes */}
        <rect width={W} height={H} fill="url(#cbg1)" />
        <rect width={W} height={H} fill="url(#cbg2)" />
        <rect width={W} height={H} fill="url(#cbg3)" />

        {/* Base grid */}
        <g stroke="var(--circuit-grid)" strokeWidth="0.5">
          {Array.from({ length: 25 }, (_, i) => (
            <line key={`hg${i}`} x1={0} y1={i * 40} x2={W} y2={i * 40} />
          ))}
          {Array.from({ length: 37 }, (_, i) => (
            <line key={`vg${i}`} x1={i * 40} y1={0} x2={i * 40} y2={H} />
          ))}
        </g>

        {/* Chip bodies */}
        {chips.map((chip, i) => (
          <g key={`chip${i}`}>
            <rect
              x={chip.x - chip.bodyW / 2}
              y={chip.y - chip.bodyH / 2}
              width={chip.bodyW}
              height={chip.bodyH}
              rx="3"
              fill="var(--circuit-chip)"
              stroke="var(--circuit-chip-stroke)"
              strokeWidth="1"
            />
            {/* Inner detail */}
            <rect
              x={chip.x - chip.bodyW / 2 + 4}
              y={chip.y - chip.bodyH / 2 + 4}
              width={chip.bodyW - 8}
              height={chip.bodyH - 8}
              rx="1"
              fill="none"
              stroke="var(--circuit-detail)"
              strokeWidth="0.5"
            />
            {/* Chip label area */}
            <rect
              x={chip.x - chip.bodyW / 2 + 8}
              y={chip.y - chip.bodyH / 2 + 6}
              width={chip.bodyW - 16}
              height={chip.bodyH - 12}
              rx="1"
              fill="var(--circuit-detail-fill)"
            />
            {/* Pins */}
            {Array.from({ length: chip.cols }, (_, c) =>
              Array.from({ length: 2 }, (_, side) => {
                const px =
                  chip.x -
                  chip.bodyW / 2 +
                  chip.bodyPad +
                  chip.pinW / 2 +
                  c * (chip.pinW + chip.pinGap);
                const py =
                  side === 0
                    ? chip.y - chip.bodyH / 2 - chip.pinH
                    : chip.y + chip.bodyH / 2;
                return (
                  <rect
                    key={`pin${c}${side}`}
                    x={px - chip.pinW / 2}
                    y={py}
                    width={chip.pinW}
                    height={chip.pinH}
                    fill="var(--circuit-pin)"
                    rx="0.5"
                  />
                );
              })
            )}
          </g>
        ))}

        {/* Via / node dots */}
        {viaNodes.map((vn, i) => (
          <circle
            key={`vn${i}`}
            cx={vn.cx}
            cy={vn.cy}
            r={vn.r}
            fill="none"
            stroke="var(--circuit-node)"
            strokeWidth="0.8"
            className="via-node"
          />
        ))}

        {/* Traces */}
        <g fill="none" stroke="var(--circuit-trace)" strokeWidth="1" filter="url(#glow)">
          {traces.map((t, i) => (
            <path key={`trace${i}`} d={t.d} />
          ))}
        </g>

        {/* Flow particles */}
        {traces.map((t, i) => (
          <circle
            key={`particle${i}`}
            r="2"
            fill="var(--circuit-particle)"
            filter="url(#glowStrong)"
            className="flow-particle"
          >
            <animateMotion
              dur={`${t.duration}s`}
              repeatCount="indefinite"
              begin={`${t.delay}s`}
              path={t.d}
            />
          </circle>
        ))}

        {/* Pulse rings on some vias */}
        {viaNodes.filter(() => rand() > 0.6).map((vn, i) => (
          <circle
            key={`pulse${i}`}
            cx={vn.cx}
            cy={vn.cy}
            r={vn.r}
            fill="none"
            stroke="var(--circuit-particle)"
            strokeWidth="1"
            filter="url(#glowStrong)"
            className="pulse-ring"
          />
        ))}
      </svg>
    </div>
  );
}
